import React, { useReducer, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils.js';
import { formatISO, parseISO, addDays } from 'date-fns';
import swal from 'sweetalert';
import { toast } from 'sonner';
import PageTitle from '../../../layouts/PageTitle';
import NewArticleModal from '../Factoring/Articles/NewArticleModal.js';
import NewCustomerModal from '../Factoring/Customers/NewCustomerModal.js';
import { get, isEqual } from 'lodash';

// Enhanced Components
import InvoiceActionsBootstrap from './newinvoiceform/InvoiceActionsBootstrap.jsx';
import TemplateSelectorBootstrap from './newinvoiceform/TemplateSelectorBootstrap.jsx';
import InvoiceFormBootstrap from './newinvoiceform/InvoiceFormBootstrap.jsx';
import CustomerSelectionCard from './CustomerSelectionCard.js';
import DeliveryOptionsCard from './newinvoiceform/DeliveryOptionsCard.jsx';
import PaymentMethodsCard from './newinvoiceform/PaymentMethodsCard.jsx';

import { useInvoiceSettings } from '../../../../context/InvoiceSettingsContext';
import { useChangeTracking } from './hooks/useChangeTracking';
import { invoiceReducer, createInitialState } from './reducers/invoiceReducers.jsx';
import { validationSchema } from './utils/invoiceValidation.jsx';

const transformApiItemsToInternal = (apiItems = []) => {
  return apiItems.map((item, index) => ({
    id: item.item_id || `item-${index}`, // API uses 'item_id'
    productcode: item.productcode || '', // This field exists in API
    description: item.description || '', // This field exists in API
    unitprice: parseFloat(item.unitprice || 0), // API uses 'unitprice' as string
    quantity: parseFloat(item.quantity || 1),
    unit: item.unit || 'st', // API has 'unit' field
    tax_rate: item.tax_rate || '25%', // API uses 'tax_rate'
    discount: parseFloat(item.discount || 0),
    type: item.type || 'Product', // API has 'type' field
    item_id: item.item_id, // Keep original API ID for updates
    article_id: item.article_id, // Keep article reference
    isNew: false // Mark as existing item
  }));
};

// Helper function to normalize data for comparison
const normalizeDataForComparison = (state) => {
  return {
    invoiceData: {
      customerid: state.customerid,
      customer: state.customer ? {
        id: state.customer.id,
        name: state.customer.name,
        email: state.customer.email
      } : null,
      invoiceNumber: state.invoiceNumber,
      invoiceDate: state.invoiceDate?.getTime(),
      dueDate: state.dueDate?.getTime(),
      currency: state.currency,
      roundTotal: state.roundTotal,
      notes: state.notes,
      status: state.status,
      selectedTemplate: state.selectedTemplate
    },
    items: state.items.map(item => ({
      id: item.id,
      productcode: item.productcode || '',
      description: item.description || '',
      unitprice: Number(item.unitprice) || 0,
      quantity: Number(item.quantity) || 1,
      unit: item.unit || 'st',
      tax_rate: item.tax_rate || '25%',
      discount: Number(item.discount) || 0,
      type: item.type || 'Product'
    })),
    deliveryOptions: state.deliveryOptions,
    paymentMethods: state.paymentMethods
  };
};

const InvoiceForm = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();
  const isEditMode = Boolean(invoiceId);

  // Refs and contexts
  const invoiceRef = useRef(null);
  const { invoiceSettings } = useInvoiceSettings();

  // Initialize state with reducer
  const [state, dispatch] = useReducer(
    invoiceReducer,
    { invoiceId, isEditMode, invoiceSettings },
    ({ invoiceId, isEditMode, invoiceSettings }) => createInitialState(invoiceId, isEditMode, invoiceSettings)
  );

  // Additional state
  const [articles, setArticles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewArticleModal, setShowNewArticleModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Change tracking - only start after data is loaded
  const trackedData = useMemo(() => {
    if (!isDataLoaded) return null;
    return normalizeDataForComparison(state);
  }, [state, isDataLoaded]);

  const { hasChanges, resetTracking, getChangedFields } = useChangeTracking(trackedData);

  // Validation
  const validationErrors = useMemo(() => {
    const errors = {};
    Object.keys(validationSchema).forEach(field => {
      const error = validationSchema[field](state[field], state);
      if (error) errors[field] = error;
    });
    return errors;
  }, [state]);

  const isFormValid = useMemo(() => {
    return Object.keys(validationErrors).length === 0 && state.customerid && state.items.length > 0;
  }, [validationErrors, state.customerid, state.items]);

  const hasCriticalErrors = useMemo(() => {
    const criticalFields = ['customerid', 'invoiceDate', 'dueDate', 'currency'];
    const hasCriticalValidationErrors = criticalFields.some(field => validationErrors[field]);
    const hasNoCustomer = !state.customerid;
    const hasNoItems = !state.items || state.items.length === 0;
    const hasNoDeliveryMethod = !state.deliveryOptions?.method;

    // For existing invoices (edit mode), don't require payment methods to be set
    // Only require them when actually publishing (which is handled in handlePublish)
    const hasNoPaymentMethods = !isEditMode && !Object.values(state.paymentMethods || {}).some(method => method);

    return hasCriticalValidationErrors || hasNoCustomer || hasNoItems || hasNoDeliveryMethod || hasNoPaymentMethods;
  }, [validationErrors, state.customerid, state.items, state.deliveryOptions?.method, state.paymentMethods, isEditMode]);

  // Data fetching methods
  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        getApiUrl(`${companyId}/invoices/${invoiceId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        const { data: invoice } = response.data;

        console.log('Raw API invoice response:', invoice);
        console.log('Delivery fields in API response:', {
          delivery_method: invoice.delivery_method,
          delivery_email: invoice.delivery_email,
          delivery_phone: invoice.delivery_phone,
          delivery_subject: invoice.delivery_subject,
          delivery_message: invoice.delivery_message,
          payment_methods: invoice.payment_methods,
          payment_methods_type: typeof invoice.payment_methods,
          payment_methods_parsed: invoice.payment_methods ? (typeof invoice.payment_methods === 'string' ? 'will_parse' : 'already_parsed') : 'empty',
          selected_template: invoice.selected_template,
          notes: invoice.notes
        });

        // Parse payment methods from stored data
        let paymentMethods = {};
        if (invoice.payment_methods) {
          try {
            let methods = invoice.payment_methods;

            // If it's a string, parse it as JSON
            if (typeof methods === 'string') {
              methods = JSON.parse(methods);
            }

            // Convert to object format for the UI
            if (Array.isArray(methods)) {
              // Convert array to object format
              methods.forEach(method => {
                paymentMethods[method] = true;
              });
              console.log('Converted payment methods array to object:', paymentMethods);
            } else if (typeof methods === 'object' && methods !== null) {
              paymentMethods = methods;
              console.log('Using payment methods object directly:', paymentMethods);
            }
          } catch (e) {
            console.warn('Failed to parse payment methods:', e, 'Raw value:', invoice.payment_methods);
            paymentMethods = {};
          }
        }

        // Parse delivery options from stored fields
        const deliveryOptions = {
          method: invoice.delivery_method || 'download',
          email: invoice.delivery_email || '',
          phone: invoice.delivery_phone || '',
          message: invoice.delivery_message || '',
          subject: invoice.delivery_subject || 'Faktura'
        };

        console.log('Final parsed data for frontend:', {
          deliveryOptions,
          paymentMethods,
          selectedTemplate: invoice.selected_template
        });

        // Get default payment days from invoice settings
        const defaultPaymentDays = invoiceSettings?.defaultTerms?.paymentDays || 30;

        // First, create the basic transformed invoice WITH stored delivery options
        const transformedInvoice = {
          invoiceId: invoice.invoiceid,
          customerid: invoice.customerid,
          invoiceNumber: invoice.invoicenumber || '',
          invoiceDate: invoice.invoicedate ? parseISO(invoice.invoicedate) : new Date(),
          dueDate: invoice.deadline ? parseISO(invoice.deadline) : addDays(new Date(), defaultPaymentDays),
          currency: invoice.currency || 'SEK',
          roundTotal: Boolean(invoice.rounding),
          status: invoice.status || 'Draft',
          notes: invoice.notes || '',
          selectedTemplate: invoice.selected_template || 'classic',
          items: transformApiItemsToInternal(invoice.items || []),
          deliveryOptions: deliveryOptions,
          paymentMethods: paymentMethods
        };

        // Load the basic invoice data first
        console.log('Dispatching LOAD_INVOICE with payment methods:', transformedInvoice.paymentMethods);
        dispatch({ type: 'LOAD_INVOICE', payload: transformedInvoice, invoiceSettings });

        // Handle customer separately
        if (transformedInvoice.customerid) {
          const customerFromInvoice = {
            id: invoice.customerid,
            name: invoice.customername,
            email: invoice.email,
            phone: invoice.phone,
            customer_number: invoice.kundnummer,
            type: invoice.orgnumber ? 'company' : 'person',
            orgnumber: invoice.orgnumber,
            addresses: [{
              address_type: 'billing',
              street1: invoice.street1,
              city: invoice.city,
              postal_code: invoice.postalcode,
              country: invoice.country || 'Sverige'
            }]
          };

          try {
            const fullCustomer = await fetchCustomerDetails(transformedInvoice.customerid);
            const customerToUse = fullCustomer || customerFromInvoice;

            // Set customer first
            dispatch({ type: 'SET_CUSTOMER', payload: customerToUse });

            // Then update delivery options separately
            const customerEmail = customerToUse.email ||
              (customerToUse.contacts && customerToUse.contacts.length > 0
                ? customerToUse.contacts[0].contact_email
                : '');

            const customerPhone = customerToUse.phone ||
              (customerToUse.contacts && customerToUse.contacts.length > 0
                ? customerToUse.contacts[0].contact_phone
                : '');



            console.log('Using customer data from API:', {
              customer: customerToUse,
              id: customerToUse.id,
              name: customerToUse.name,
              email: customerEmail,
              phone: customerPhone
            });



            // Update delivery options after customer is set, but only fill empty fields
            dispatch({
              type: 'UPDATE_DELIVERY_OPTIONS',
              payload: {
                email: deliveryOptions.email || customerEmail,
                phone: deliveryOptions.phone || customerPhone,
                method: deliveryOptions.method,
                message: deliveryOptions.message,
                subject: deliveryOptions.subject
              }
            });

          } catch (error) {
            console.log('Using customer data from invoice response');
            dispatch({ type: 'SET_CUSTOMER', payload: customerFromInvoice });
          }
        }

        // Mark data as loaded after all operations complete
        setTimeout(() => {
          console.log('Final state after loading invoice:', {
            paymentMethods: state.paymentMethods,
            deliveryOptions: state.deliveryOptions,
            selectedTemplate: state.selectedTemplate
          });
          setIsDataLoaded(true);
          setIsInitialized(true);
          setTimeout(() => resetTracking(), 100);
        }, 200); // Longer delay to ensure all state updates complete
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Fel', {
        description: 'Kunde inte hämta fakturadetaljer'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/articles`),
        { withCredentials: true }
      );

      setArticles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers`),
        { withCredentials: true }
      );
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const saveNewCustomer = async (customerData) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/sales/customers`),
        customerData,
        { withCredentials: true }
      );

      console.log('Customer creation response:', response.data);

      // Refresh customers list first
      await fetchCustomers();

      // Close modal
      setShowNewCustomerModal(false);

      // Show success message
      toast.success('Framgång!', {
        description: 'Kunden har skapats.'
      });

      // Try to find and auto-select the newly created customer
      // We'll do this after the customers list is refreshed
      setTimeout(async () => {
        try {
          // Get the updated customers list and find the newly created one
          const customersResponse = await axios.get(
            getApiUrl(`${companyId}/sales/customers`),
            { withCredentials: true }
          );
          const updatedCustomers = customersResponse.data || [];

          // Find the customer that matches the created data
          let newCustomer = null;

          if (response.data && response.data.customerId) {
            // API returns customerId in the response
            newCustomer = updatedCustomers.find(c => c.id === response.data.customerId);
            console.log('Searching for customer with ID:', response.data.customerId);
          } else if (response.data && response.data.data && response.data.data.id) {
            // Alternative API structure
            newCustomer = updatedCustomers.find(c => c.id === response.data.data.id);
            console.log('Searching for customer with ID (alt structure):', response.data.data.id);
          } else {
            // Fallback: find by unique identifiers
            console.log('Using fallback matching by customer data');
            newCustomer = updatedCustomers.find(c => {
              if (customerData.type === 'company') {
                return (c.orgnumber && c.orgnumber === customerData.orgnumber) ||
                  (c.name && c.name === customerData.name);
              } else {
                // For individuals, the API might combine first_name + last_name into name field
                const fullName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();
                return (c.name && c.name === fullName) ||
                  (c.first_name === customerData.first_name && c.last_name === customerData.last_name);
              }
            });
          }

          if (newCustomer) {
            console.log('Auto-selecting customer:', newCustomer);
            handleFormDataChange({ customer: newCustomer });
          } else {
            console.log('Could not find newly created customer for auto-selection');
          }
        } catch (error) {
          console.error('Error auto-selecting customer:', error);
        }
      }, 500); // Small delay to ensure state updates

      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Fel!', {
        description: 'Ett fel uppstod när kunden skulle skapas.'
      });
      throw error;
    }
  };

  // Debug effect to track payment methods changes
  useEffect(() => {
    if (isEditMode && state.paymentMethods) {
      console.log('Payment methods changed in state:', state.paymentMethods);
    }
  }, [state.paymentMethods, isEditMode]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchArticles(),
        fetchCustomers()
      ]);

      if (isEditMode && invoiceId) {
        await fetchInvoiceDetails();
      } else {
        // For new invoices, mark as initialized immediately
        setIsDataLoaded(true);
        setIsInitialized(true);
        setTimeout(() => resetTracking(), 100);
      }
    };

    loadInitialData();
  }, [invoiceId, isEditMode]);

  // Form handlers
  const handleFormDataChange = useCallback((updates) => {
    console.log('Form data change:', updates);

    if ('customer' in updates) {
      dispatch({ type: 'SET_CUSTOMER', payload: updates.customer });

      // Auto-populate delivery options with customer contact information
      if (updates.customer) {
        const customerEmail = updates.customer.email ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_email
            : '');

        const customerPhone = updates.customer.phone ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_phone
            : '');

        // Use setTimeout to ensure customer is set before updating delivery options
        setTimeout(() => {
          dispatch({
            type: 'UPDATE_DELIVERY_OPTIONS',
            payload: {
              email: customerEmail,
              phone: customerPhone,
              method: state.deliveryOptions?.method || 'download',
              message: state.deliveryOptions?.message || '',
              subject: state.deliveryOptions?.subject || 'Faktura'
            }
          });
        }, 50);

        console.log('Auto-populated delivery options:', {
          email: customerEmail,
          phone: customerPhone
        });
      }
    } else if ('items' in updates) {
      dispatch({ type: 'SET_ITEMS', payload: updates.items });
    } else {
      dispatch({ type: 'UPDATE_INVOICE_DATA', payload: updates });
    }
  }, []);

  const handleDeliveryMethodChange = useCallback((method) => {
    dispatch({
      type: 'UPDATE_DELIVERY_OPTIONS',
      payload: { method }
    });
  }, []);

  const handleDeliveryDetailsChange = useCallback((updates) => {
    dispatch({
      type: 'UPDATE_DELIVERY_OPTIONS',
      payload: updates
    });
  }, []);

  const handlePaymentMethodChange = useCallback((method) => {
    dispatch({ type: 'TOGGLE_PAYMENT_METHOD', payload: method });
  }, []);

  // Update the handleSaveDraft function:
  const handleSaveDraft = useCallback(async () => {
    if (!state.customerid) {
      toast.error('Fel', {
        description: 'Vänligen välj en kund'
      });
      return null;
    }

    if (state.items.length === 0) {
      toast.error('Fel', {
        description: 'Lägg till minst en artikel'
      });
      return null;
    }

    setLoading(true);
    try {
      const dataToSend = {
        customer_id: state.customerid,
        invoice_number: state.invoiceNumber,
        invoice_date: formatISO(state.invoiceDate, { representation: 'date' }),
        due_date: formatISO(state.dueDate, { representation: 'date' }),
        currency: state.currency,
        round_total: state.roundTotal,
        notes: state.notes,
        status: 'Draft',
        selected_template: state.selectedTemplate,
        items: state.items.map(item => ({
          article_id: item.article_id,
          product_code: item.productcode,
          description: item.description,
          unit_price: item.unitprice,
          quantity: item.quantity,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount: item.discount || 0,
          type: item.type || 'Product',
          item_id: item.id && !item.isNew ? item.id : undefined
        })),
        delivery_options: state.deliveryOptions,
        payment_methods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m])
      };

      console.log('Data to send for saving:', dataToSend);
      console.log('Current state before save:', state);

      let response;

      if (isEditMode) {
        response = await axios.put(
          getApiUrl(`${companyId}/invoices/${invoiceId}`),
          dataToSend,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          getApiUrl(`${companyId}/invoices`),
          dataToSend,
          { withCredentials: true }
        );
      }

      if (response.data.success) {
        // FIXED: Reset change tracking IMMEDIATELY and SYNCHRONOUSLY before navigation
        resetTracking();

        toast.success('Klart', {
          description: isEditMode ? 'Fakturan uppdaterad' : 'Fakturan sparad'
        });

        const savedInvoiceId = response.data.invoiceId || invoiceId;

        // If creating new invoice, redirect to edit mode
        // The resetTracking() above ensures hasChanges is false before navigation
        if (!isEditMode && savedInvoiceId) {
          history.replace(`/${companyId}/edit-invoice/${savedInvoiceId}`);
        }

        return savedInvoiceId;
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fel', {
        description: error.response?.data?.message || 'Kunde inte spara fakturan'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [state, isEditMode, companyId, invoiceId, resetTracking, history]);

  const handlePublish = useCallback(async () => {
    if (!state.deliveryOptions.method) {
      toast.warning('Varning', {
        description: 'Vänligen välj ett leveransalternativ'
      });
      return;
    }

    const hasSelectedPaymentMethod = Object.values(state.paymentMethods).some(v => v);
    if (!hasSelectedPaymentMethod) {
      toast.warning('Varning', {
        description: 'Vänligen välj minst en betalningsmetod'
      });
      return;
    }

    if (!isFormValid) {
      toast.warning('Varning', {
        description: 'Vänligen åtgärda valideringsfel'
      });
      return;
    }

    let invoiceIdToPublish = invoiceId;

    // Save changes before publishing if needed
    if (hasChanges || !isEditMode) {
      const confirmSave = await swal({
        title: hasChanges ? 'Osparade ändringar' : 'Spara och publicera',
        text: hasChanges
          ? 'Vill du spara utkastet innan du publicerar fakturan?'
          : 'Fakturan måste sparas innan den kan publiceras. Fortsätt?',
        icon: 'warning',
        buttons: ['Avbryt', 'Spara och publicera'],
      });

      if (confirmSave) {
        invoiceIdToPublish = await handleSaveDraft();
        if (!invoiceIdToPublish) return;
      } else {
        return;
      }
    }

    try {
      setLoading(true);

      console.log('Publishing invoice with ID:', invoiceIdToPublish, state.deliveryOptions);


      const response = await axios.post(
        getApiUrl(`${companyId}/invoices/${invoiceIdToPublish}/publish`),
        {
          action: state.deliveryOptions.method,
          email: state.deliveryOptions.email,
          phone: state.deliveryOptions.phone,
          subject: state.deliveryOptions.subject,
          message: state.deliveryOptions.message,
          paymentMethods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m]),
        },
        {
          withCredentials: true,
          responseType: state.deliveryOptions.method === 'download' ? 'blob' : 'json'
        }
      );

      if (state.deliveryOptions.method === 'download' && response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `faktura-${invoiceIdToPublish}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      }

      dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { status: 'Sent' } });
      resetTracking();
      toast.success('Klart', {
        description: 'Fakturan publicerad'
      });
      history.push(`/${companyId}/invoices`);
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Fel', {
        description: 'Kunde inte publicera fakturan'
      });
    } finally {
      setLoading(false);
    }
  }, [state, hasChanges, isFormValid, isEditMode, invoiceId, companyId, handleSaveDraft, resetTracking, history]);

  // Add this function with the other handlers
  const handleDeleteDraft = useCallback(async () => {
    if (!isEditMode || state.status !== 'Draft') {
      return;
    }

    const confirmed = await swal({
      title: "Är du säker?",
      text: "Vill du radera detta fakturautkast? Detta kan inte ångras.",
      icon: "warning",
      buttons: ["Avbryt", "Radera utkast"],
      dangerMode: true,
    });

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        getApiUrl(`${companyId}/invoices/${invoiceId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        //swal("Raderad!", "Fakturautkastet har raderats.", "success");
        resetTracking(); // Reset tracking to prevent navigation warnings
        history.push(`/${companyId}/invoices`);
      } else {
        throw new Error(response.data.message || 'Något gick fel');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error("Fel!", {
        description: "Kunde inte radera fakturautkastet. Försök igen senare."
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, invoiceId, isEditMode, state.status, history, resetTracking]);

  // Article management
  const handleSaveArticle = async (newArticle) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/articles`),
        newArticle,
        { withCredentials: true }
      );
      if (response.data.success) {
        await fetchArticles();
        toast.success('Klart', {
          description: 'Artikeln sparades'
        });
      }
    } catch (error) {
      toast.error('Fel', {
        description: 'Kunde inte spara artikeln'
      });
    }
    setShowNewArticleModal(false);
  };

  // Update the navigation warning effect:
  useEffect(() => {
    const unblock = history.block((location) => {
      // Only show warning for user-initiated navigation, not programmatic navigation
      // Check if this is a programmatic navigation (like after saving)
      if (hasChanges && isInitialized) {
        // If we're navigating to the edit version of the same invoice, allow it
        const isNavigatingToEditSameInvoice = location.pathname.includes(`/edit-invoice/`) &&
          !isEditMode;

        if (isNavigatingToEditSameInvoice) {
          return true; // Allow navigation without prompt
        }

        return new Promise((resolve) => {
          swal({
            title: 'Du har osparade ändringar!',
            text: 'Vill du spara utkastet innan du lämnar sidan?',
            icon: 'warning',
            buttons: {
              cancel: 'Avbryt',
              discard: {
                text: 'Fortsätt utan att spara',
                value: 'discard',
              },
              save: {
                text: 'Spara utkast',
                value: 'save',
              },
            },
          }).then((value) => {
            switch (value) {
              case 'save':
                handleSaveDraft().then((savedId) => {
                  resolve(savedId ? true : false);
                });
                break;
              case 'discard':
                resolve(true);
                break;
              default:
                resolve(false);
                break;
            }
          });
        });
      }
      return true;
    });

    return () => unblock();
  }, [hasChanges, isInitialized, history, handleSaveDraft, isEditMode]);

  return (
    <>
      <div className="d-flex flex-column h-100">
        <PageTitle
          activeMenu={isEditMode ? "Redigera Faktura" : "Skapa Faktura"}
          motherMenu="Fakturor"
        />

        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                {isEditMode ? 'Redigera Faktura' : 'Skapa Ny Faktura'}
                {state.status && (
                  <span className={`badge ms-3 ${state.status === 'Draft' ? 'bg-warning' : 'bg-success'}`}>
                    {state.status === 'Draft' ? 'Utkast' : 'Skickad'}
                  </span>
                )}
                {isEditMode && state.invoiceNumber && (
                  <span className="badge bg-info ms-2">#{state.invoiceNumber}</span>
                )}
              </h4>
              <p className="text-muted mb-0">
                {isEditMode
                  ? 'Uppdatera fakturanuppgifter och innehåll'
                  : 'Fyll i kundinformation, lägg till produkter och välj leveransalternativ'
                }
                {hasChanges && isInitialized && <span className="text-warning ms-2">(Osparade ändringar)</span>}
              </p>
            </div>

            <div className="d-flex gap-2">
              <Link
                to={`/${companyId}/invoices`}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Tillbaka
              </Link>
            </div>
          </div>
        </div>

        {/* Actions Section */}
        <InvoiceActionsBootstrap
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onDelete={handleDeleteDraft}
          isValid={isFormValid}
          hasUnsavedChanges={hasChanges && isInitialized}
          hasCriticalErrors={hasCriticalErrors}
          loading={loading}
          showDeleteButton={isEditMode && state.status === 'Draft'}
        />

        {/* Customer Selection Section - Now standalone */}
        <CustomerSelectionCard
          customers={customers}
          selectedCustomer={state.customer}
          onCustomerSelect={(customer) => handleFormDataChange({ customer })}
          onFetchCustomerDetails={fetchCustomerDetails}
          onCreateCustomer={() => setShowNewCustomerModal(true)}
          loading={loading}
        />

        {/* Template Selection */}
        {state.customer && (
          <div className="mb-4">
            <TemplateSelectorBootstrap
              selectedTemplate={state.selectedTemplate}
              onSelectTemplate={(template) => dispatch({ type: 'SET_TEMPLATE', payload: template })}
              availableTemplates={invoiceSettings?.templates}
            />
          </div>
        )}

        {/* Delivery Options */}
        {state.customer && (
          <DeliveryOptionsCard
            deliveryMethod={state.deliveryOptions.method}
            onDeliveryMethodChange={handleDeliveryMethodChange}
            deliveryDetails={state.deliveryOptions}
            onDeliveryDetailsChange={handleDeliveryDetailsChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Payment Methods */}
        {state.customer && (
          <PaymentMethodsCard
            paymentMethods={state.paymentMethods}
            onPaymentMethodChange={handlePaymentMethodChange}
            invoiceSettings={invoiceSettings}
          />
        )}

        {/* Main Form Content */}
        {state.customer && (
          <div className="flex-grow-1 h-100">
            <InvoiceFormBootstrap
              formData={{
                customerid: state.customerid,
                customer: state.customer,
                invoiceNumber: state.invoiceNumber,
                invoiceDate: state.invoiceDate,
                dueDate: state.dueDate,
                currency: state.currency,
                roundTotal: state.roundTotal,
                items: state.items,
                notes: state.notes,
                defaultVatRate: state.defaultVatRate
              }}
              onFormDataChange={handleFormDataChange}
              availableArticles={articles}
              loading={loading}
              invoiceSettings={invoiceSettings}
              validationErrors={validationErrors}
            />
          </div>
        )}

        {/* New Article Modal */}
        <NewArticleModal
          show={showNewArticleModal}
          handleClose={() => setShowNewArticleModal(false)}
          handleSave={handleSaveArticle}
          articleDetails={selectedArticle}
        />

        {/* New Customer Modal */}
        <NewCustomerModal
          show={showNewCustomerModal}
          handleClose={() => setShowNewCustomerModal(false)}
          handleSave={saveNewCustomer}
        />
      </div>
    </>
  );
};

export default InvoiceForm;
